import React, { useState, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  Stack,
  TextField,
  IconButton,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";

import {
  createTaskReview,
  updateExistingTaskStatus,
} from "store/features/company/projectManagement/task/taskActions";

const TaskReviewModal = ({
  open,
  onClose,
  taskData,
  pendingStatusChange,
  loadTasks,
}) => {
  const dispatch = useDispatch();

  const [review, setReview] = useState("");
  const [reviewPurpose, setReviewPurpose] = useState("");
  const [deliverableFile, setDeliverableFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setReviewPurpose("General");
    } else {
      setReview("");
      setReviewPurpose("");
      setDeliverableFile(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!review.trim()) {
      return;
    }

    setSubmitting(true);

    try {
      // Build FormData because deliverables is a file upload
      const reviewPayload = new FormData();
      reviewPayload.append("review", review.trim());
      reviewPayload.append("review_purpose", reviewPurpose || "General");
      if (deliverableFile) {
        reviewPayload.append("deliverables", deliverableFile);
      }

      const reviewResult = await dispatch(
        createTaskReview(taskData.taskId, reviewPayload),
      );

      if (!reviewResult.success) {
        setSubmitting(false);
        return;
      }

      const statusResult = await dispatch(
        updateExistingTaskStatus(pendingStatusChange.taskId, {
          status: pendingStatusChange.status,
        }),
      );

      if (!statusResult.success) {
        setSubmitting(false);
        return;
      }

      setReview("");
      setReviewPurpose("");
      setDeliverableFile(null);

      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box
        sx={{
          background: "linear-gradient(135deg,#f59e0b 0%,#d97706 100%)",
          height: 6,
        }}
      />

      <Box
        sx={{
          p: 3,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 2,
          }}
        >
          Add Review
        </Typography>
        <Typography
          sx={{
            mb: 2,
            color: "text.secondary",
          }}
        >
          Status will be changed to <strong>{taskData?.status}</strong>
        </Typography>

        {/* ── Review ── */}
        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Review"
          placeholder="e.g. Task completed successfully"
          value={review}
          onChange={(e) => setReview(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* ── Review Purpose ── */}
        <TextField
          fullWidth
          label="Review Purpose"
          placeholder="e.g. General"
          value={reviewPurpose}
          onChange={(e) => setReviewPurpose(e.target.value)}
          helperText="Auto-filled by default — change if needed"
          sx={{ mb: 2 }}
        />

        {/* ── Deliverables (Optional) ── */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="body2"
            sx={{ mb: 1, color: "text.secondary", fontWeight: 500 }}
          >
            Deliverables (Optional)
          </Typography>

          {!deliverableFile ? (
            <Box
              onClick={() =>
                document.getElementById("deliverable-upload").click()
              }
              sx={{
                border: "2px dashed",
                borderColor: "divider",
                borderRadius: "12px",
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "#f59e0b",
                  backgroundColor: "rgba(245,158,11,0.05)",
                },
              }}
            >
              <input
                id="deliverable-upload"
                type="file"
                accept=".csv,.xlsx,.xls,.pdf,.jpeg,.jpg,.png,.doc,.docx,.zip"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setDeliverableFile(file);
                }}
                style={{ display: "none" }}
              />
              <Typography variant="body2" color="text.secondary">
                Click to upload a deliverable file
              </Typography>
              <Typography variant="caption" color="text.disabled">
                CSV, XLSX, XLS, PDF, IMG, DOC, ZIP (Max 5MB)
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "action.hover",
              }}
            >
              <Box flex={1} minWidth={0}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {deliverableFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(deliverableFile.size / 1024).toFixed(1)} KB
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setDeliverableFile(null);
                  const el = document.getElementById("deliverable-upload");
                  if (el) el.value = "";
                }}
                sx={{
                  "&:hover": { color: "#ef4444" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ mt: 3 }}
        >
          <Button variant="outlined" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={submitting || !review.trim()}
            onClick={handleSubmit}
          >
            Submit Review
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default TaskReviewModal;
